import { map, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  LiveStreamSendItemApi,
  UpcomingShowSendItemApi,
} from '../../generated/api';
import { SendItem, SendItemStatus } from '../../models/ClientModels';
import { hasAny } from '../../utils/utils';
import { EntityRef, entityRefSelect } from '../EntityRef';
import { firestore } from '../firebase/firebaseAccess';
import {
  collectionQuery,
  docQuery,
  snapshotToDataWithId,
  WithID,
} from '../firebase/rxfireExtensions';

export class SendItemServiceHelper {
  constructor(
    private liveStreamSendItemService: LiveStreamSendItemApi,
    private upcomingShowSendItemService: UpcomingShowSendItemApi,
  ) {}

  async rejectSendItem(
    showRef: SendItemAvailableShowRef,
    sendItemId: string,
  ): Promise<void> {
    await entityRefSelect(showRef, {
      liveShow: s =>
        this.liveStreamSendItemService.liveStreamSendItemApiControllerRejectSendItem(
          {
            id: sendItemId,
            liveStreamId: s.liveStreamId,
            closeSendItemRequest: {},
          },
        ),
      upcomingShow: s =>
        this.upcomingShowSendItemService.upcomingShowSendItemApiControllerRejectSendItem(
          {
            id: sendItemId,
            upcomingShowId: s.upcomingShowId,
            upcomingShowCloseSendItemRequest: {},
          },
        ),
    });
  }

  getSendItemById(
    showRef: SendItemAvailableShowRef,
    sendItemId: string,
  ): Observable<undefined | WithID<SendItem>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('sendItems')
          .doc(sendItemId),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('sendItems')
          .doc(sendItemId),
    });

    return docQuery(query).pipe(
      map(snap =>
        snap.data() != null ? snapshotToDataWithId(snap)! : undefined,
      ),
    );
  }

  getReceivingItem(
    showRef: SendItemAvailableShowRef,
    receiverId: string,
  ): Observable<WithID<SendItem>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('sendItems')
          .where('toUserId', '==', receiverId)
          .where('status', '==', SendItemStatus.PROCESSING)
          .limit(1),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('sendItems')
          .where('toUserId', '==', receiverId)
          .where('status', '==', SendItemStatus.PROCESSING)
          .limit(1),
    });

    return collectionQuery(query).pipe(
      map(snap => snap.docs),
      filter(hasAny),
      map(docs => snapshotToDataWithId(docs[0])!),
    );
  }
}

export type SendItemAvailableShowRef =
  | EntityRef.LiveShow
  | EntityRef.UpcomingShow;
export function isSendItemAvailableShowRef(
  showRef: EntityRef,
): showRef is SendItemAvailableShowRef {
  return showRef.type === 'liveShow' || showRef.type === 'upcomingShow';
}
